import { Injectable, Signal, signal } from '@angular/core';

import { OCCUPATION_FORMS, OccupationForm } from './models/occupation.model';

/** The two places the chart appears; each remembers its own form. */
export type OccupationSurface = 'popup' | 'module';

const FORM_PREFIX = 'occupation-form-';
const LEGEND_PREFIX = 'occupation-legend-collapsed-';

function read(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    // Private mode, or site data blocked. The default is a fine answer.
    return null;
  }
}

function write(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Persistence is a convenience; losing it must never break the control.
  }
}

function readStoredForm(surface: OccupationSurface): OccupationForm | null {
  const raw = read(`${FORM_PREFIX}${surface}`);
  return OCCUPATION_FORMS.includes(raw as OccupationForm) ? (raw as OccupationForm) : null;
}

@Injectable({ providedIn: 'root' })
export class OccupationFormService {
  private readonly forms: Record<
    OccupationSurface,
    ReturnType<typeof signal<OccupationForm>>
  > = {
    popup: signal<OccupationForm>(readStoredForm('popup') ?? 'area'),
    module: signal<OccupationForm>(readStoredForm('module') ?? 'area')
  };

  // Expanded until folded: the explanation is only new once, but it is new the first time.
  private readonly legendCollapsed: Record<
    OccupationSurface,
    ReturnType<typeof signal<boolean>>
  > = {
    popup: signal(read(`${LEGEND_PREFIX}popup`) === 'true'),
    module: signal(read(`${LEGEND_PREFIX}module`) === 'true')
  };

  formFor(surface: OccupationSurface): Signal<OccupationForm> {
    return this.forms[surface].asReadonly();
  }

  set(surface: OccupationSurface, form: OccupationForm): void {
    this.forms[surface].set(form);
    write(`${FORM_PREFIX}${surface}`, form);
  }

  cycle(surface: OccupationSurface): void {
    const current = this.forms[surface]();
    const next =
      OCCUPATION_FORMS[(OCCUPATION_FORMS.indexOf(current) + 1) % OCCUPATION_FORMS.length];
    this.set(surface, next);
  }

  legendCollapsedFor(surface: OccupationSurface): Signal<boolean> {
    return this.legendCollapsed[surface].asReadonly();
  }

  toggleLegend(surface: OccupationSurface): void {
    const next = !this.legendCollapsed[surface]();
    this.legendCollapsed[surface].set(next);
    write(`${LEGEND_PREFIX}${surface}`, String(next));
  }
}
